import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SmtpSettingInputSchema, type SmtpSettingInput } from '@app/shared';
import { useSmtpSetting, useTestSmtp, useUpdateSmtp } from './smtpApi';
import { getApiErrorMessage } from '../../lib/api-client';

export function SmtpForm(): JSX.Element {
  const { data, isLoading, isError, error } = useSmtpSetting();
  const updateMutation = useUpdateSmtp();
  const testMutation = useTestSmtp();
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SmtpSettingInput>({
    resolver: zodResolver(SmtpSettingInputSchema),
    defaultValues: {
      host: '',
      port: 587,
      secure: false,
      user: '',
      password: '',
      fromAddress: '',
      fromName: '',
      enabled: true,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        host: data.host,
        port: data.port,
        secure: data.secure,
        user: data.user,
        password: '', // 不回填明文；留空 = 保留原密碼
        fromAddress: data.fromAddress,
        fromName: data.fromName,
        enabled: data.enabled,
      });
    }
  }, [data, reset]);

  if (isLoading) return <div role="status" className="text-slate-500">載入中...</div>;
  if (isError) {
    return (
      <div role="alert" className="text-red-600">
        載入失敗：{getApiErrorMessage(error, '未知錯誤')}
      </div>
    );
  }

  const onSubmit = (values: SmtpSettingInput): void => {
    setSavedFlash(null);
    updateMutation.mutate(values, {
      onSuccess: () => {
        setSavedFlash('已儲存');
        reset({ ...values, password: '' });
      },
    });
  };

  const serverErrorMsg = (err: unknown): string => getApiErrorMessage(err, '未知錯誤');

  return (
    <section className="grid gap-4 max-w-xl">
      <h2 className="text-lg font-semibold">SMTP 寄信</h2>
      <p className="text-sm text-slate-600">
        此設定套用於「忘記密碼」等系統寄信。密碼以 AES-256-GCM 加密儲存；留空代表保留原密碼。
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3 bg-white border rounded p-4">
        <Row label="主機 Host" error={errors.host?.message}>
          <input {...register('host')} className={inputCls} placeholder="smtp.example.com" />
        </Row>
        <Row label="埠號 Port" error={errors.port?.message}>
          <input
            type="number"
            {...register('port', { valueAsNumber: true })}
            className={`${inputCls} w-32`}
          />
        </Row>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('secure')} /> 啟用 TLS（SMTPS，通常 port 465）
        </label>
        <Row label="帳號 User" error={errors.user?.message}>
          <input {...register('user')} className={inputCls} autoComplete="off" />
        </Row>
        <Row
          label={`密碼 Password${data?.hasPassword ? '（已設定，留空保留原值）' : ''}`}
          error={errors.password?.message}
        >
          <input
            type="password"
            {...register('password')}
            className={inputCls}
            autoComplete="new-password"
            placeholder={data?.hasPassword ? '（留空保留原密碼）' : '（未設定）'}
          />
        </Row>
        <Row label="寄件人 Email" error={errors.fromAddress?.message}>
          <input {...register('fromAddress')} className={inputCls} placeholder="noreply@example.com" />
        </Row>
        <Row label="寄件人顯示名稱" error={errors.fromName?.message}>
          <input {...register('fromName')} className={inputCls} placeholder="Interview System" />
        </Row>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" {...register('enabled')} /> 啟用寄信
        </label>

        {updateMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            儲存失敗：{serverErrorMsg(updateMutation.error)}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">
            {savedFlash}
          </div>
        ) : null}

        <div className="flex gap-2 items-center">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {updateMutation.isPending ? '儲存中…' : '儲存'}
          </button>
          <button
            type="button"
            disabled={testMutation.isPending || isDirty}
            title={isDirty ? '請先儲存再測試' : '對您自己的 email 寄送一封測試信'}
            onClick={() => testMutation.mutate()}
            className="border border-blue-600 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
          >
            {testMutation.isPending ? '寄送中…' : '寄出測試信'}
          </button>
        </div>

        {testMutation.isSuccess && testMutation.data ? (
          <div role="status" className="text-green-700 text-sm">
            ✓ {testMutation.data.message}
            {testMutation.data.messageId ? `（${testMutation.data.messageId}）` : ''}
          </div>
        ) : null}
        {testMutation.isError ? (
          <div role="alert" className="text-red-600 text-sm">
            測試失敗：{serverErrorMsg(testMutation.error)}
          </div>
        ) : null}
      </form>
    </section>
  );
}

function Row({
  label,
  error,
  children,
}: {
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-slate-700">{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-red-600 text-xs">
          {error}
        </span>
      ) : null}
    </label>
  );
}

const inputCls =
  'border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';
